/**
 * useVisualViewportRect — mobile overlay viewport tracking
 *
 * Overview:
 * The configuration selector uses two mobile overlays that must stay within
 * the visible viewport when the browser UI (e.g. address bar) shows or hides:
 * 1. "Choose your layout" drawer (bottom sheet)
 * 2. "View details" fullscreen gallery panel
 *
 * Both subscribe to visualViewport 'resize' and 'scroll' only while the
 * overlay is open (enabled=true). Performance is kept in check by:
 * - Coalescing updates with requestAnimationFrame (at most one state update per frame)
 * - Calling setState only when top/left/width/height actually change
 * - Removing listeners as soon as the overlay closes
 */

import {useEffect, useRef, useState} from 'react';

/**
 * Returns the visual viewport rect (top, left, width, height).
 * Used when visualViewport is not available (e.g. SSR).
 */
function getVisualViewportRect() {
  if (typeof window === 'undefined') return null;

  const vv = window.visualViewport;

  if (vv) {
    return {
      height: vv.height,
      left: vv.offsetLeft,
      top: vv.offsetTop,
      width: vv.width,
    };
  }

  return {
    height: window.innerHeight,
    left: 0,
    top: 0,
    width: window.innerWidth,
  };
}

/**
 * Tracks the visual viewport rect for mobile overlays (drawer / fullscreen panel)
 * so content is not cut off when the browser UI (e.g. address bar) shows or hides.
 *
 * Performance:
 * - Listeners run only while `enabled` is true (e.g. drawer/panel open).
 * - Updates are coalesced with requestAnimationFrame so at most one state update
 *   per frame when resize/scroll fire repeatedly.
 * - setState is called only when the rect actually changes (top/left/width/height),
 *   avoiding redundant re-renders when the viewport has settled.
 *
 * @param {boolean} enabled - When true, subscribe to visualViewport and return rect; when false, return null and do not subscribe.
 * @returns {({ height: number, left: number, top: number, width: number } | null)} Current visual viewport rect when enabled, else null.
 */
export function useVisualViewportRect(enabled) {
  const [rect, setRect] = useState(getVisualViewportRect);
  const rafIdRef = useRef(null);
  const lastRectRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    lastRectRef.current = null;

    const updateRect = () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        const next = getVisualViewportRect();

        if (!next) return;

        const last = lastRectRef.current;

        if (
          last &&
          last.top === next.top &&
          last.left === next.left &&
          last.width === next.width &&
          last.height === next.height
        ) {
          return;
        }

        lastRectRef.current = next;
        setRect(next);
      });
    };

    updateRect();
    window.visualViewport?.addEventListener('resize', updateRect);
    window.visualViewport?.addEventListener('scroll', updateRect);

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }

      window.visualViewport?.removeEventListener('resize', updateRect);
      window.visualViewport?.removeEventListener('scroll', updateRect);
    };
  }, [enabled]);

  return enabled ? rect : null;
}

export {getVisualViewportRect};
